/**
 * Exploratieve factoranalyse (PCA-extractie + varimax-rotatie) op een
 * item-correlatiematrix. Geen externe libraries: Jacobi-eigendecompositie
 * voor symmetrische matrices is klein en volledig testbaar.
 * Pure functions — no Firebase imports.
 */
import pearsonCorrelation from './correlation'

const MAX_JACOBI_SWEEPS = 100
const JACOBI_EPS = 1e-12
const VARIMAX_EPS = 1e-8
const MAX_VARIMAX_ITER = 100

/**
 * Jacobi-eigendecompositie van een symmetrische matrix.
 *
 * @param {number[][]} matrix - Symmetrische matrix (n × n)
 * @returns {{values: number[], vectors: number[][]}} Eigenwaarden aflopend gesorteerd; vectors[i] hoort bij values[i]
 */
export const jacobiEigen = (matrix) => {
    const n = matrix.length
    const a = matrix.map((row) => [...row])
    const v = Array.from({ length: n }, (_, i) =>
        Array.from({ length: n }, (_, j) => (i === j ? 1 : 0))
    )

    for (let sweep = 0; sweep < MAX_JACOBI_SWEEPS; sweep += 1) {
        let offDiagonal = 0
        for (let p = 0; p < n - 1; p += 1) {
            for (let q = p + 1; q < n; q += 1) offDiagonal += a[p][q] ** 2
        }
        if (offDiagonal < JACOBI_EPS) break

        for (let p = 0; p < n - 1; p += 1) {
            for (let q = p + 1; q < n; q += 1) {
                if (Math.abs(a[p][q]) < JACOBI_EPS) continue

                const theta = (a[q][q] - a[p][p]) / (2 * a[p][q])
                const t = (theta >= 0 ? 1 : -1) / (Math.abs(theta) + Math.sqrt(theta ** 2 + 1))
                const c = 1 / Math.sqrt(t ** 2 + 1)
                const s = t * c

                const app = a[p][p]
                const aqq = a[q][q]
                const apq = a[p][q]
                a[p][p] = c * c * app - 2 * s * c * apq + s * s * aqq
                a[q][q] = s * s * app + 2 * s * c * apq + c * c * aqq
                a[p][q] = 0
                a[q][p] = 0

                for (let i = 0; i < n; i += 1) {
                    if (i !== p && i !== q) {
                        const aip = a[i][p]
                        const aiq = a[i][q]
                        a[i][p] = c * aip - s * aiq
                        a[p][i] = a[i][p]
                        a[i][q] = s * aip + c * aiq
                        a[q][i] = a[i][q]
                    }
                    const vip = v[i][p]
                    const viq = v[i][q]
                    v[i][p] = c * vip - s * viq
                    v[i][q] = s * vip + c * viq
                }
            }
        }
    }

    // Kolommen van v zijn de eigenvectoren; sorteer op eigenwaarde aflopend
    const order = Array.from({ length: n }, (_, i) => i)
        .sort((i, j) => a[j][j] - a[i][i])

    return {
        values: order.map((i) => a[i][i]),
        vectors: order.map((i) => v.map((row) => row[i])),
    }
}

// Varimax: roteer factorparen tot de ladingen "simple structure" benaderen
// (per item één dominante factor). Klassiek pairwise algoritme.
/** @param {number[][]} loadings - items × factoren */
const varimax = (loadings) => {
    const p = loadings.length
    const m = loadings[0]?.length || 0
    if (m < 2) return loadings

    const rotated = loadings.map((row) => [...row])

    for (let iter = 0; iter < MAX_VARIMAX_ITER; iter += 1) {
        let totalRotation = 0

        for (let i = 0; i < m - 1; i += 1) {
            for (let j = i + 1; j < m; j += 1) {
                let sumU = 0
                let sumV = 0
                let sumUV = 0
                let sumU2V2 = 0
                for (let item = 0; item < p; item += 1) {
                    const x = rotated[item][i]
                    const y = rotated[item][j]
                    const u = x * x - y * y
                    const w = 2 * x * y
                    sumU += u
                    sumV += w
                    sumUV += u * w
                    sumU2V2 += u * u - w * w
                }
                const num = 2 * (p * sumUV - sumU * sumV)
                const den = p * sumU2V2 - (sumU ** 2 - sumV ** 2)
                const phi = 0.25 * Math.atan2(num, den)
                if (Math.abs(phi) < VARIMAX_EPS) continue

                totalRotation += Math.abs(phi)
                const cos = Math.cos(phi)
                const sin = Math.sin(phi)
                for (let item = 0; item < p; item += 1) {
                    const x = rotated[item][i]
                    const y = rotated[item][j]
                    rotated[item][i] = cos * x + sin * y
                    rotated[item][j] = -sin * x + cos * y
                }
            }
        }

        if (totalRotation < VARIMAX_EPS) break
    }

    return rotated
}

/**
 * Item × item correlatiematrix uit brede dataset-rijen, pairwise-complete.
 * Niet-berekenbare cellen (te weinig paren, nulvariantie) worden 0.
 *
 * @param {Array<{scores: Object<string, number|null>}>} rows - Brede rijen (buildMsiResearchDataset)
 * @param {string[]} itemKeys - Kolomsleutels in matrixvolgorde
 * @returns {{matrix: number[][], pairCounts: number[][]}}
 */
export const buildItemCorrelationMatrix = (rows, itemKeys) => {
    const list = Array.isArray(rows) ? rows : []
    const k = itemKeys.length
    const matrix = Array.from({ length: k }, () => Array(k).fill(0))
    const pairCounts = Array.from({ length: k }, () => Array(k).fill(0))

    for (let i = 0; i < k; i += 1) {
        matrix[i][i] = 1
        pairCounts[i][i] = list.filter((row) => Number.isFinite(row?.scores?.[itemKeys[i]])).length

        for (let j = i + 1; j < k; j += 1) {
            const pairs = list
                .map((row) => ({ x: row?.scores?.[itemKeys[i]], y: row?.scores?.[itemKeys[j]] }))
                .filter((pair) => Number.isFinite(pair.x) && Number.isFinite(pair.y))
            const { r, n } = pearsonCorrelation(pairs, { minPairs: 2 })
            matrix[i][j] = r === null ? 0 : r
            matrix[j][i] = matrix[i][j]
            pairCounts[i][j] = n
            pairCounts[j][i] = n
        }
    }

    return { matrix, pairCounts }
}

/**
 * @typedef {object} FactorAnalysisResult
 * @property {number[]} eigenvalues - Alle eigenwaarden, aflopend
 * @property {number[]} explained - Verklaarde variantie-proportie per eigenwaarde
 * @property {number[][]} loadings - Varimax-geroteerde ladingen (items × factoren)
 * @property {number} numFactors - Aantal geëxtraheerde factoren
 */

/**
 * @param {number[][]} correlationMatrix - Symmetrische item-correlatiematrix
 * @param {object} [options]
 * @param {number} [options.numFactors] - Aantal factoren; default Kaiser-criterium (eigenwaarde > 1, minimaal 1)
 * @returns {FactorAnalysisResult}
 */
const factorAnalysis = (correlationMatrix, { numFactors } = {}) => {
    const k = correlationMatrix.length
    const { values, vectors } = jacobiEigen(correlationMatrix)

    const factors = numFactors != null
        ? Math.max(1, Math.min(numFactors, k))
        : Math.max(1, values.filter((value) => value > 1).length)

    // Ongeroteerde ladingen: eigenvector × √eigenwaarde
    const unrotated = Array.from({ length: k }, (_, item) =>
        Array.from({ length: factors }, (_, f) =>
            vectors[f][item] * Math.sqrt(Math.max(values[f], 0))
        )
    )

    return {
        eigenvalues: values,
        explained: values.map((value) => value / k),
        loadings: varimax(unrotated),
        numFactors: factors,
    }
}

export default factorAnalysis
